import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Modal, Tip } from '@ofself/shell'
import { useChat } from '../contexts/ChatContext'
import { uploadFile } from '../services/api'

const CHAT_API = import.meta.env.VITE_CHAT_API_URL || ''
import { decodeAudioToSamples, drawWaveform } from '../utils/audioWaveform'
import EmojiPickerPanel, { trackEmojiUsage } from './EmojiPickerPanel'
import PluginPicker from './PluginPicker'

// Convert HTML from contentEditable to markdown for storage
function htmlToMarkdown(html) {
  const div = document.createElement('div')
  div.innerHTML = html

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return ''

    const tag = node.tagName.toLowerCase()

    // Mention chips: extract @username text
    if (node.classList?.contains('mention-chip-input')) {
      return node.textContent
    }

    const inner = Array.from(node.childNodes).map(walk).join('')

    switch (tag) {
      case 'b': case 'strong': return `**${inner}**`
      case 'i': case 'em': return `_${inner}_`
      case 'u': return `__${inner}__`
      case 's': case 'strike': case 'del': return `~~${inner}~~`
      case 'code': return `\`${inner}\``
      case 'pre': return `\`\`\`\n${node.textContent}\n\`\`\``
      case 'a': {
        const href = node.getAttribute('href') || ''
        return `[${inner}](${href})`
      }
      case 'br': return '\n'
      case 'div': case 'p': {
        // Divs/paragraphs are line breaks (Chrome wraps lines in divs)
        const text = inner
        if (!text) return '\n'
        return '\n' + text
      }
      case 'ol': {
        const items = Array.from(node.querySelectorAll(':scope > li'))
        return '\n' + items.map((li, i) => `${i + 1}. ${walk(li)}`).join('\n')
      }
      case 'ul': {
        const items = Array.from(node.querySelectorAll(':scope > li'))
        return '\n' + items.map(li => `- ${walk(li)}`).join('\n')
      }
      case 'li': return inner
      default: return inner
    }
  }

  const result = Array.from(div.childNodes).map(walk).join('')
  // Clean up leading newline and multiple consecutive newlines
  return result.replace(/^\n+/, '').replace(/\n{3,}/g, '\n\n')
}

// Clear the editor AND land the caret back inside it. `innerHTML = ''` alone
// leaves the selection anchored to the removed nodes whenever the caret sat
// in nested markup (a list <li>, a <pre>): the editor keeps focus but every
// keystroke targets a detached node — the dead-composer-after-sending-a-list
// bug. Re-anchoring a collapsed range in the now-empty editor makes the next
// keystroke land normally. (Plain text never hit this: the caret sits in the
// editor's own text child, and the browser remaps that removal correctly.)
function clearEditor(editor) {
  editor.innerHTML = ''
  const sel = window.getSelection()
  if (!sel) return
  const range = document.createRange()
  range.selectNodeContents(editor)
  range.collapse(true)
  sel.removeAllRanges()
  sel.addRange(range)
}


// `doors` (a ref): the conversation's end rail opens the composer through it, as
// the Mac's rail does: attach() the file picker, insert(text) words from Voice,
// and the +'s actions (format, emoji, mention, audio, video), which the rail
// then holds in the +'s place (owner call 2026-10-30).
export default function MessageInput({ onSend: onSendProp, placeholder: placeholderProp, doors = null }) {
  const { sendMessage, activeChannel, members, activeMessages, currentUser, emitTyping, editMessage, editingMessage, setEditingMessage, replyingToMessage, setReplyingToMessage } = useChat()
  const [hasContent, setHasContent] = useState(false)
  const [showFormatting, setShowFormatting] = useState(false)
  // The +'s action strip (Format/Emoji/Mention/Audio/Video), for a composer whose
  // doors no rail holds (a thread's, the phone's): [+] [message…] [send]. Where the
  // conversation's end rail stands, these are its seats and the + is gone.
  const [actionsOpen, setActionsOpen] = useState(false)
  const [pendingFile, setPendingFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [linkText, setLinkText] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [activeFormats, setActiveFormats] = useState({})
  const [showMentions, setShowMentions] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIndex, setMentionIndex] = useState(0)
  const [showPluginPicker, setShowPluginPicker] = useState(false)
  const [pluginQuery, setPluginQuery] = useState('')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [recording, setRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [reviewBlob, setReviewBlob] = useState(null)
  const [reviewSamples, setReviewSamples] = useState(null)
  const [reviewDuration, setReviewDuration] = useState(0)
  const [reviewPlaying, setReviewPlaying] = useState(false)
  const [reviewProgress, setReviewProgress] = useState(0)
  // Video recording state
  const [videoModal, setVideoModal] = useState(null) // 'setup' | 'recording' | 'review' | null
  const [videoTime, setVideoTime] = useState(0)
  const [videoBlob, setVideoBlob] = useState(null)
  const [videoSource, setVideoSource] = useState('camera') // 'camera' | 'screen'
  const emojiBtnRef = useRef(null)
  const editorRef = useRef(null)
  const fileInputRef = useRef(null)
  const actionsRef = useRef(null)
  const plusRef = useRef(null)
  const composerRef = useRef(null)

  // Close the + action strip on any click outside it (the + toggles itself).
  useEffect(() => {
    if (!actionsOpen) return
    function onDown(e) {
      if (actionsRef.current?.contains(e.target) || plusRef.current?.contains(e.target)) return
      setActionsOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [actionsOpen])

  // Same contract for the emoji drawer: the panel is a pure view, so the host
  // that floats it decides when it goes away (the 🙂 button toggles itself).
  useEffect(() => {
    if (!showEmojiPicker) return
    function onDown(e) {
      // the 🙂 that toggles it: the strip's, or the end rail's Emoji seat
      if (emojiBtnRef.current?.contains(e.target) || e.target.closest?.("[data-seat='emoji']")) return
      if (e.target.closest?.('.emoji-picker-portal')) return
      setShowEmojiPicker(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [showEmojiPicker])
  const dragCounter = useRef(0)
  // Save selection range before opening link modal
  const savedRange = useRef(null)
  // Voice recording refs
  const mediaRecorderRef = useRef(null)
  const audioCtxRef = useRef(null)
  const analyserRef = useRef(null)
  const waveformCanvasRef = useRef(null)
  const samplesRef = useRef([])
  const animFrameRef = useRef(null)
  const timerRef = useRef(null)
  const chunksRef = useRef([])
  // Voice review refs
  const reviewAudioRef = useRef(null)
  const reviewCanvasRef = useRef(null)
  const reviewRafRef = useRef(null)
  // Video recording refs
  const videoRecorderRef = useRef(null)
  const videoStreamRef = useRef(null)
  const videoTimerRef = useRef(null)
  const videoChunksRef = useRef([])
  const videoPreviewRef = useRef(null)
  const videoReviewRef = useRef(null)

  function checkActiveFormats() {
    const sel = window.getSelection()
    const editor = editorRef.current
    // Only check formats when selection is inside the editor
    if (!editor || !sel.rangeCount || !editor.contains(sel.anchorNode)) {
      setActiveFormats({})
      return
    }
    const formats = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strike: document.queryCommandState('strikeThrough'),
      ol: document.queryCommandState('insertOrderedList'),
      ul: document.queryCommandState('insertUnorderedList'),
    }
    // Check if cursor is inside a <code> or <pre>
    let node = sel.anchorNode
    while (node && node !== editor) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase()
        if (tag === 'code') formats.code = true
        if (tag === 'pre') formats.codeblock = true
      }
      node = node.parentNode
    }
    setActiveFormats(formats)
  }

  useEffect(() => {
    document.addEventListener('selectionchange', checkActiveFormats)
    return () => document.removeEventListener('selectionchange', checkActiveFormats)
  }, [])

  useEffect(() => {
    // preventScroll: focusing the composer must NOT scroll the canvas — in responsive
    // mode the board is a scroll container, and a plain focus() yanks it down to the
    // composer on load (the "refreshes to the bottom" bug).
    editorRef.current?.focus({ preventScroll: true })
  }, [activeChannel?.id])

  // Focus editor when replying to a message
  useEffect(() => {
    if (replyingToMessage) {
      editorRef.current?.focus({ preventScroll: true })
    }
  }, [replyingToMessage])

  // When editing a message, populate the editor with the message content
  useEffect(() => {
    if (editingMessage && editorRef.current) {
      editorRef.current.innerText = editingMessage.content
      editorRef.current.focus({ preventScroll: true })
      setHasContent(true)
      // Move cursor to end
      const range = document.createRange()
      range.selectNodeContents(editorRef.current)
      range.collapse(false)
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
    }
  }, [editingMessage])

  // Close emoji picker on outside click
  function insertEmoji(emoji) {
    trackEmojiUsage(emoji)
    const editor = editorRef.current
    if (!editor) return
    editor.focus()
    document.execCommand('insertText', false, emoji)
    // Defer check so DOM updates before we test emoji-only
    requestAnimationFrame(() => updateHasContent())
    setShowEmojiPicker(false)
  }

  // Words from elsewhere (the rail's Voice) land where the caret is, or after
  // what is written when the caret is not in the editor; the emoji's insert path.
  function insertText(text) {
    const editor = editorRef.current
    if (!editor || !text) return
    editor.focus({ preventScroll: true })
    const sel = window.getSelection()
    if (!sel.rangeCount || !editor.contains(sel.anchorNode)) {
      const range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    }
    const before = editor.textContent.replace(/\u200B/g, '')
    document.execCommand('insertText', false, before && !/\s$/.test(before) ? ` ${text}` : text)
    requestAnimationFrame(() => updateHasContent())
  }

  // THE DOORS, HANDED OUT to the end rail (ComposerInput's contract)
  useLayoutEffect(() => {
    if (!doors) return undefined
    doors.current = {
      attach: () => fileInputRef.current?.click(),
      insert: insertText,
      format: () => setShowFormatting((f) => !f),
      emoji: () => { setShowMentions(false); setShowEmojiPicker((v) => !v) },
      mention: openMentionPopup,
      audio: () => { setShowMentions(false); setShowEmojiPicker(false); startRecording() },
      video: openVideoModal,
    }
    return () => { doors.current = null }
  })

  // Drag-and-drop
  useEffect(() => {
    const mainPanel = document.querySelector('.conversation')
    if (!mainPanel) return
    function handleDragEnter(e) {
      e.preventDefault(); e.stopPropagation()
      dragCounter.current++
      if (e.dataTransfer?.types?.includes('Files')) setDragging(true)
    }
    function handleDragLeave(e) {
      e.preventDefault(); e.stopPropagation()
      dragCounter.current--
      if (dragCounter.current === 0) setDragging(false)
    }
    function handleDragOver(e) { e.preventDefault(); e.stopPropagation() }
    function handleDrop(e) {
      e.preventDefault(); e.stopPropagation()
      dragCounter.current = 0; setDragging(false)
      const file = e.dataTransfer?.files?.[0]
      if (file) setPendingFile(file)
    }
    mainPanel.addEventListener('dragenter', handleDragEnter)
    mainPanel.addEventListener('dragleave', handleDragLeave)
    mainPanel.addEventListener('dragover', handleDragOver)
    mainPanel.addEventListener('drop', handleDrop)
    return () => {
      mainPanel.removeEventListener('dragenter', handleDragEnter)
      mainPanel.removeEventListener('dragleave', handleDragLeave)
      mainPanel.removeEventListener('dragover', handleDragOver)
      mainPanel.removeEventListener('drop', handleDrop)
    }
  }, [])

  const execFormat = useCallback((command, value) => {
    editorRef.current?.focus()
    document.execCommand(command, false, value || null)
    updateHasContent()
    setTimeout(checkActiveFormats, 0)
  }, [])

  const applyFormat = useCallback((type) => {
    switch (type) {
      case 'bold': execFormat('bold'); break
      case 'italic': execFormat('italic'); break
      case 'underline': execFormat('underline'); break
      case 'strike': execFormat('strikeThrough'); break
      case 'code': {
        // Wrap selection in <code> tag
        const sel = window.getSelection()
        if (sel.rangeCount > 0) {
          const range = sel.getRangeAt(0)
          const selectedText = range.toString()
          const code = document.createElement('code')
          code.className = 'editor-inline-code'
          code.textContent = selectedText || '\u200B'
          range.deleteContents()
          range.insertNode(code)
          // Move cursor after the code element
          range.setStartAfter(code)
          range.collapse(true)
          sel.removeAllRanges()
          sel.addRange(range)
        }
        updateHasContent()
        break
      }
      case 'codeblock': {
        const sel = window.getSelection()
        if (sel.rangeCount > 0) {
          const range = sel.getRangeAt(0)

          // Toggle OFF: if cursor is already inside a <pre>, unwrap it
          let preAncestor = sel.anchorNode
          while (preAncestor && preAncestor !== editorRef.current) {
            if (preAncestor.nodeType === Node.ELEMENT_NODE && preAncestor.tagName === 'PRE') break
            preAncestor = preAncestor.parentNode
          }
          if (preAncestor && preAncestor.tagName === 'PRE') {
            // Replace the <pre> with its plain text content
            const text = preAncestor.textContent || ''
            const textNode = document.createTextNode(text === '\u200B' ? '' : text)
            preAncestor.parentNode.replaceChild(textNode, preAncestor)
            // Place cursor at end of unwrapped text
            const newRange = document.createRange()
            newRange.setStart(textNode, textNode.length)
            newRange.collapse(true)
            sel.removeAllRanges()
            sel.addRange(newRange)
            updateHasContent()
            break
          }

          // Toggle ON: wrap selection in a code block
          const selectedText = range.toString()
          const pre = document.createElement('pre')
          pre.className = 'editor-codeblock'
          pre.textContent = selectedText || '\u200B'
          range.deleteContents()
          range.insertNode(pre)
          // Place cursor inside the code block
          const textNode = pre.firstChild
          range.setStart(textNode, selectedText ? selectedText.length : 0)
          range.collapse(true)
          sel.removeAllRanges()
          sel.addRange(range)
        }
        updateHasContent()
        break
      }
      case 'link': {
        // Save current selection before opening modal
        const sel = window.getSelection()
        if (sel.rangeCount > 0) {
          savedRange.current = sel.getRangeAt(0).cloneRange()
          setLinkText(sel.toString() || '')
        } else {
          savedRange.current = null
          setLinkText('')
        }
        setLinkUrl('')
        setShowLinkModal(true)
        break
      }
      case 'ol': execFormat('insertOrderedList'); break
      case 'ul': execFormat('insertUnorderedList'); break
      default: break
    }
  }, [execFormat])

  const handleLinkSave = useCallback(() => {
    if (!linkUrl.trim()) return
    const displayText = linkText.trim() || linkUrl.trim()
    const editor = editorRef.current

    if (editor) {
      editor.focus()
      // Restore saved selection
      if (savedRange.current) {
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(savedRange.current)
        // Delete selected text
        savedRange.current.deleteContents()
      }
      // Insert link
      const a = document.createElement('a')
      a.href = linkUrl.trim()
      a.textContent = displayText
      a.className = 'editor-link'
      a.target = '_blank'
      a.rel = 'noopener noreferrer'

      const sel = window.getSelection()
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0)
        range.insertNode(a)
        range.setStartAfter(a)
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
      } else {
        editor.appendChild(a)
      }
      // Add space after link
      document.execCommand('insertText', false, ' ')
    }

    setShowLinkModal(false)
    setLinkText('')
    setLinkUrl('')
    savedRange.current = null
    updateHasContent()
  }, [linkText, linkUrl])

  // Handle paste: force plain text inside code blocks, and plain text everywhere
  // to avoid rich HTML breaking the editor layout
  function handlePaste(e) {
    e.preventDefault()
    // a pasted file or screenshot attaches, as a dropped one does
    const file = e.clipboardData.files?.[0]
    if (file) { setPendingFile(file); return }
    const text = e.clipboardData.getData('text/plain')
    if (!text) return

    const sel = window.getSelection()
    if (!sel.rangeCount) return

    // Check if cursor is inside a <pre> (code block)
    let node = sel.anchorNode
    let insidePre = false
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'PRE') {
        insidePre = true
        break
      }
      node = node.parentNode
    }

    if (insidePre) {
      // Insert as plain text node inside the code block
      const textNode = document.createTextNode(text)
      const range = sel.getRangeAt(0)
      range.deleteContents()
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
    } else {
      // Outside code block: insert as plain text (preserving newlines as <br>)
      const lines = text.split('\n')
      const range = sel.getRangeAt(0)
      range.deleteContents()
      const frag = document.createDocumentFragment()
      lines.forEach((line, i) => {
        frag.appendChild(document.createTextNode(line))
        if (i < lines.length - 1) frag.appendChild(document.createElement('br'))
      })
      range.insertNode(frag)
      // Move cursor to end of pasted content
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    }
    updateHasContent()
  }

  // Get text before cursor in the contentEditable editor
  function getTextBeforeCursor() {
    const editor = editorRef.current
    if (!editor) return ''
    const sel = window.getSelection()
    if (!sel.rangeCount) return ''
    try {
      const range = sel.getRangeAt(0).cloneRange()
      range.collapse(true)
      range.setStart(editor, 0)
      return range.toString()
    } catch {
      // Fallback: use full text content
      return (editor.innerText || editor.textContent || '').replace(/\n+$/, '')
    }
  }

  const mentionForceOpen = useRef(false)

  function checkForMention() {
    // Skip if mention was just opened via button (prevent onInput from closing it)
    if (mentionForceOpen.current) {
      mentionForceOpen.current = false
      return
    }
    const text = getTextBeforeCursor()
    const atMatch = text.match(/@(\w*)$/)
    if (atMatch) {
      setShowMentions(true)
      setMentionQuery(atMatch[1].toLowerCase())
      setMentionIndex(0)
    } else {
      setShowMentions(false)
    }
  }

  function checkForSlashCommand() {
    // Only show picker when entire input is "/<query>" with no spaces.
    // Do NOT trim — we need to detect trailing spaces/nbsp to dismiss.
    const rawText = (editorRef.current?.textContent || '').replace(/\u00A0/g, ' ')
    const fullText = rawText.trim()
    // Dismiss if raw text has any whitespace (user pressed space) or doesn't start with /
    if (rawText !== fullText || /\s/.test(fullText) || !fullText.startsWith('/')) {
      setShowPluginPicker(false)
      return
    }
    const slashMatch = fullText.match(/^\/(\S*)$/)
    if (slashMatch) {
      setShowPluginPicker(true)
      setPluginQuery(slashMatch[1].toLowerCase())
    } else {
      setShowPluginPicker(false)
    }
  }

  async function handlePluginSelect(item) {
    setShowPluginPicker(false)
    const editor = editorRef.current
    if (!editor) return

    editor.textContent = ''
    updateHasContent()

    if (item.type === 'persona') {
      // Persona: use slug (or sanitised name) as the command key
      const cmdKey = item.slug || (item.name || '').toLowerCase().replace(/\s+/g, '-')
      editor.textContent = `/${cmdKey} `
      updateHasContent()
      editor.dataset.selectedPersonaId   = item.id
      editor.dataset.selectedPersonaName = item.name
      editor.dataset.selectedPersonaSlug = cmdKey
    } else {
      // Plugin (existing behaviour)
      editor.textContent = `/${item.sub_entity_key} `
      updateHasContent()
      editor.dataset.selectedPluginId  = item.plugin_id
      editor.dataset.selectedPluginKey = item.sub_entity_key
    }

    // Focus at end
    const range = document.createRange()
    range.selectNodeContents(editor)
    range.collapse(false)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
  }

  function openMentionPopup() {
    // Toggle: if already showing, close it
    if (showMentions) {
      setShowMentions(false)
      return
    }
    const editor = editorRef.current
    if (!editor) return
    editor.focus()
    setShowEmojiPicker(false)
    mentionForceOpen.current = true
    const text = editor.textContent || ''
    if (!text.endsWith('@')) {
      document.execCommand('insertText', false, '@')
    }
    setShowMentions(true)
    setMentionQuery('')
    setMentionIndex(0)
    updateHasContent()
  }

  const mentionOptions = (() => {
    if (!showMentions) return []
    const options = [{ user_id: 'everyone', username: 'everyone' }]
    const channelMembers = (members || []).filter(m => m.user_id !== currentUser?.id)
    options.push(...channelMembers)
    if (!mentionQuery) return options
    return options.filter(m => m.username.toLowerCase().includes(mentionQuery))
  })()

  function insertMention(username) {
    const sel = window.getSelection()
    const editor = editorRef.current
    if (!editor) return
    editor.focus()

    // Strategy: delete back to the @ symbol, then insert a styled mention chip
    const textBefore = getTextBeforeCursor()
    const atIdx = textBefore.lastIndexOf('@')
    if (atIdx !== -1) {
      const charsToDelete = textBefore.length - atIdx
      for (let i = 0; i < charsToDelete; i++) {
        document.execCommand('delete', false)
      }
    }
    // Insert a styled mention span + trailing space
    if (sel.rangeCount) {
      const range = sel.getRangeAt(0)
      range.deleteContents()
      const mentionSpan = document.createElement('span')
      mentionSpan.className = 'mention-chip-input'
      mentionSpan.contentEditable = 'false'
      mentionSpan.textContent = `@${username}`
      mentionSpan.setAttribute('data-mention', username)
      range.insertNode(mentionSpan)
      // Add a space after the mention and place cursor there
      const space = document.createTextNode('\u00A0')
      mentionSpan.after(space)
      range.setStartAfter(space)
      range.setEndAfter(space)
      sel.removeAllRanges()
      sel.addRange(range)
    }

    setShowMentions(false)
    setMentionQuery('')
    updateHasContent()
  }

  const EMOJI_ONLY_RE = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\p{Regional_Indicator}{2})+$/u

  function updateHasContent() {
    const editor = editorRef.current
    if (!editor) return
    const text = editor.textContent.replace(/\u200B/g, '').trim()
    setHasContent(text.length > 0 || editor.querySelector('img, pre, code'))
    // Toggle big emoji in editor
    const stripped = text.replace(/\s/g, '')
    const isEmoji = stripped.length > 0 && EMOJI_ONLY_RE.test(stripped)
    const wasBig = editor.classList.contains('emoji-only')
    if (isEmoji) {
      editor.classList.add('emoji-only')
    } else {
      editor.classList.remove('emoji-only')
      // When transitioning from big → normal, strip Chrome's inline font-size junk
      if (wasBig) {
        editor.querySelectorAll('[style]').forEach(el => {
          el.style.removeProperty('font-size')
          el.style.removeProperty('line-height')
          if (!el.getAttribute('style')?.trim()) el.removeAttribute('style')
        })
        editor.querySelectorAll('font[size]').forEach(el => {
          el.removeAttribute('size')
        })
      }
    }
  }

  // ── Voice recording ──────────────────────────────────────────────────
  function cleanupRecording() {
    clearInterval(timerRef.current)
    cancelAnimationFrame(animFrameRef.current)
    if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null }
    if (mediaRecorderRef.current?.stream) {
      mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop())
    }
    mediaRecorderRef.current = null
    analyserRef.current = null
    setRecording(false)
    setRecordingTime(0)
    samplesRef.current = []
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const audioCtx = new AudioContext()
      const source = audioCtx.createMediaStreamSource(stream)
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      audioCtxRef.current = audioCtx
      analyserRef.current = analyser

      const recorder = new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      recorder.start()
      mediaRecorderRef.current = recorder

      setRecording(true)
      setRecordingTime(0)
      samplesRef.current = []
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000)

      // Waveform animation
      const dataArray = new Uint8Array(analyser.fftSize)
      let lastSample = 0
      function tick(ts) {
        const canvas = waveformCanvasRef.current
        if (!canvas || !analyserRef.current) return
        // Sample amplitude every ~60ms
        if (ts - lastSample >= 60) {
          lastSample = ts
          analyserRef.current.getByteTimeDomainData(dataArray)
          let sum = 0
          for (let i = 0; i < dataArray.length; i++) {
            const v = (dataArray[i] - 128) / 128
            sum += v * v
          }
          samplesRef.current.push(Math.sqrt(sum / dataArray.length))
        }
        // Draw
        const dpr = window.devicePixelRatio || 1
        const rect = canvas.getBoundingClientRect()
        const w = rect.width
        const h = rect.height
        if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
          canvas.width = w * dpr
          canvas.height = h * dpr
        }
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.save()
        ctx.scale(dpr, dpr)
        const barW = 2.5, gap = 1.5, step = barW + gap
        const maxBars = Math.floor(w / step)
        const visible = samplesRef.current.slice(-maxBars)
        const style = getComputedStyle(document.documentElement)
        ctx.fillStyle = style.getPropertyValue('--text-secondary')?.trim() || 'rgba(255,255,255,0.6)'
        for (let i = 0; i < visible.length; i++) {
          const barH = Math.max(2, visible[i] * (h - 4) * 2.5)
          const x = w - (visible.length - i) * step
          const y = (h - barH) / 2
          ctx.beginPath()
          ctx.roundRect(x, y, barW, barH, barW / 2)
          ctx.fill()
        }
        ctx.restore()
        animFrameRef.current = requestAnimationFrame(tick)
      }
      animFrameRef.current = requestAnimationFrame(tick)
    } catch (err) {
      console.error('Mic access denied:', err)
    }
  }

  function stopRecording() {
    const recorder = mediaRecorderRef.current
    if (!recorder || recorder.state === 'inactive') return
    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
      cleanupRecording()
      setReviewBlob(blob)
      try {
        const { samples, duration } = await decodeAudioToSamples(blob, 80)
        setReviewSamples(samples)
        setReviewDuration(duration)
      } catch (err) {
        console.warn('Audio decode failed, sending without preview:', err)
        // Fallback: send directly if decode fails (e.g. Safari + webm)
        await sendVoiceBlob(blob)
      }
    }
    recorder.stop()
  }

  async function sendVoiceBlob(blob) {
    const ext = (blob.type || '').includes('mp4') ? 'm4a' : 'webm'
    const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type })
    cleanupReview()
    setUploading(true)
    try {
      const res = await uploadFile(file)
      if (onSendProp) {
        await onSendProp('Audio clip', res.file.id)
      } else {
        sendMessage('Audio clip', res.file.id)
      }
    } catch (err) {
      console.error('Voice upload failed:', err)
    }
    setUploading(false)
  }

  async function sendReview() {
    if (!reviewBlob) return
    await sendVoiceBlob(reviewBlob)
  }

  function cleanupReview() {
    if (reviewAudioRef.current) {
      reviewAudioRef.current.pause()
      URL.revokeObjectURL(reviewAudioRef.current.src)
      reviewAudioRef.current = null
    }
    cancelAnimationFrame(reviewRafRef.current)
    setReviewBlob(null)
    setReviewSamples(null)
    setReviewDuration(0)
    setReviewPlaying(false)
    setReviewProgress(0)
  }

  function toggleReviewPlay() {
    const audio = reviewAudioRef.current
    if (!audio) return
    if (reviewPlaying) {
      audio.pause()
      cancelAnimationFrame(reviewRafRef.current)
    } else {
      audio.play()
      // Use RAF for smooth progress updates instead of choppy timeupdate
      function tick() {
        if (audio.duration) setReviewProgress(audio.currentTime / audio.duration)
        reviewRafRef.current = requestAnimationFrame(tick)
      }
      reviewRafRef.current = requestAnimationFrame(tick)
    }
    setReviewPlaying(!reviewPlaying)
  }

  // Set up review audio element when blob is available
  useEffect(() => {
    if (!reviewBlob) return
    const audio = new Audio(URL.createObjectURL(reviewBlob))
    reviewAudioRef.current = audio
    audio.addEventListener('ended', () => {
      setReviewPlaying(false)
      setReviewProgress(0)
      cancelAnimationFrame(reviewRafRef.current)
    })
    return () => { audio.pause(); URL.revokeObjectURL(audio.src) }
  }, [reviewBlob])

  // Draw review waveform
  useEffect(() => {
    const canvas = reviewCanvasRef.current
    if (!canvas || !reviewSamples) return
    const style = getComputedStyle(document.documentElement)
    drawWaveform(canvas, reviewSamples, {
      progress: reviewProgress,
      fill: true,
      playedColor: style.getPropertyValue('--accent')?.trim() || '#2d7ca8',
      unplayedColor: style.getPropertyValue('--text-faint')?.trim() || 'rgba(255,255,255,0.3)',
    })
  }, [reviewSamples, reviewProgress])

  function handleReviewCanvasClick(e) {
    const audio = reviewAudioRef.current
    const canvas = reviewCanvasRef.current
    if (!audio || !canvas || !audio.duration) return
    const rect = canvas.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * audio.duration
    setReviewProgress(ratio)
  }

  function cancelRecording() {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    cleanupRecording()
  }

  function formatRecTime(sec) {
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  // ── Video recording ─────────────────────────────────────────────────
  function openVideoModal() {
    setVideoModal('setup')
    setVideoSource('camera')
    setVideoTime(0)
    setVideoBlob(null)
  }

  // Attach stream to preview element only during setup (not recording)
  useEffect(() => {
    if (videoModal !== 'setup') return
    let cancelled = false
    async function getStream() {
      videoStreamRef.current?.getTracks().forEach(t => t.stop())
      try {
        let stream
        if (videoSource === 'screen') {
          const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true })
          try {
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
            for (const track of micStream.getAudioTracks()) displayStream.addTrack(track)
          } catch {}
          stream = displayStream
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        }
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
        videoStreamRef.current = stream
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream
          videoPreviewRef.current.play().catch(() => {})
        }
      } catch (err) {
        console.error('Media access denied:', err)
        if (!cancelled) setVideoModal(null)
      }
    }
    getStream()
    return () => { cancelled = true }
  }, [videoModal === 'setup' ? videoSource : null]) // only re-run when source changes in setup

  function startVideoRecording() {
    const stream = videoStreamRef.current
    if (!stream) return
    const recorder = new MediaRecorder(stream)
    videoChunksRef.current = []
    recorder.ondataavailable = (e) => { if (e.data.size > 0) videoChunksRef.current.push(e.data) }
    recorder.start()
    videoRecorderRef.current = recorder
    setVideoModal('recording')
    setVideoTime(0)
    videoTimerRef.current = setInterval(() => setVideoTime(t => t + 1), 1000)
  }

  function stopVideoRecording() {
    const recorder = videoRecorderRef.current
    if (!recorder || recorder.state === 'inactive') return
    recorder.onstop = () => {
      const blob = new Blob(videoChunksRef.current, { type: recorder.mimeType || 'video/webm' })
      clearInterval(videoTimerRef.current)
      videoStreamRef.current?.getTracks().forEach(t => t.stop())
      videoStreamRef.current = null
      setVideoBlob(blob)
      setVideoModal('review')
    }
    recorder.stop()
  }

  // Attach blob to review element
  useEffect(() => {
    if (videoModal !== 'review' || !videoBlob) return
    const url = URL.createObjectURL(videoBlob)
    if (videoReviewRef.current) videoReviewRef.current.src = url
    return () => URL.revokeObjectURL(url)
  }, [videoModal, videoBlob])

  function cancelVideoRecording() {
    const recorder = videoRecorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    clearInterval(videoTimerRef.current)
    videoStreamRef.current?.getTracks().forEach(t => t.stop())
    videoStreamRef.current = null
    videoRecorderRef.current = null
    setVideoModal(null)
    setVideoBlob(null)
    setVideoTime(0)
  }

  async function sendVideoClip() {
    if (!videoBlob) return
    const ext = (videoBlob.type || '').includes('mp4') ? 'mp4' : 'webm'
    const file = new File([videoBlob], `video-${Date.now()}.${ext}`, { type: videoBlob.type })
    setVideoModal(null)
    setVideoBlob(null)
    setVideoTime(0)
    setUploading(true)
    try {
      const res = await uploadFile(file)
      if (onSendProp) {
        await onSendProp('Video clip', res.file.id)
      } else {
        sendMessage('Video clip', res.file.id)
      }
    } catch (err) {
      console.error('Video upload failed:', err)
    }
    setUploading(false)
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    const editor = editorRef.current
    if (!editor) return

    const html = editor.innerHTML
    const markdown = htmlToMarkdown(html).trim()

    if (!markdown && !pendingFile) return

    let fileId = null
    if (pendingFile) {
      setUploading(true)
      try {
        const res = await uploadFile(pendingFile)
        fileId = res.file.id
      } catch (err) {
        console.error('Upload failed:', err)
        setUploading(false)
        return
      }
      setUploading(false)
    }

    // Check if this is a persona invocation (starts with /<slug>)
    const personaId   = editor.dataset.selectedPersonaId
    const personaName = editor.dataset.selectedPersonaName
    const personaSlug = editor.dataset.selectedPersonaSlug
    if (personaId && personaSlug && markdown.startsWith(`/${personaSlug}`)) {
      const personaMessage = markdown.slice(`/${personaSlug}`.length).trim()
      delete editor.dataset.selectedPersonaId
      delete editor.dataset.selectedPersonaName
      delete editor.dataset.selectedPersonaSlug
      clearEditor(editor)
      editor.classList.remove('emoji-only')
      setHasContent(false)

      if (personaMessage && activeChannel?.id) {
        // Post the user's visible message first
        sendMessage(`**/${personaName}** ${personaMessage}`)
        // Determine thread_parent_id for stateful thread context
        const threadParentId = replyingToMessage?.sender_type === 'agent'
          ? replyingToMessage.id
          : null
        setReplyingToMessage(null)
        try {
          const token = localStorage.getItem('chat_session_token')
          await fetch(`${CHAT_API}/api/chat/personas/${personaId}/invoke`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              message:          personaMessage,
              channel_id:       activeChannel.id,
              agent_name:       personaName,
              thread_parent_id: threadParentId,
            }),
          })
        } catch (err) {
          console.error('Persona invocation failed:', err)
        }
      }
      return
    }

    // Check if this is a plugin invocation (starts with /sub_entity_key)
    const pluginId = editor.dataset.selectedPluginId
    const pluginKey = editor.dataset.selectedPluginKey
    if (pluginId && markdown.startsWith(`/${pluginKey}`)) {
      const pluginMessage = markdown.slice(`/${pluginKey}`.length).trim()
      delete editor.dataset.selectedPluginId
      delete editor.dataset.selectedPluginKey
      clearEditor(editor)
      editor.classList.remove('emoji-only')
      setHasContent(false)

      // Send the user's message first, then invoke
      if (pluginMessage && activeChannel?.id) {
        sendMessage(`**/${pluginKey}** ${pluginMessage}`)
        try {
          const token = localStorage.getItem('chat_session_token')
          await fetch(`${CHAT_API}/api/chat/plugins/${pluginId}/invoke`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ message: pluginMessage, channel_id: activeChannel.id }),
          })
        } catch (err) {
          console.error('Plugin invocation failed:', err)
        }
      }
      return
    }

    if (editingMessage) {
      if (markdown && markdown !== editingMessage.content) {
        editMessage(editingMessage.id, markdown)
      }
      setEditingMessage(null)
    } else if (onSendProp) {
      await onSendProp(markdown || 'Shared a file', fileId)
    } else {
      sendMessage(markdown || 'Shared a file', fileId)
    }
    clearEditor(editor)
    editor.classList.remove('emoji-only')
    setHasContent(false)
    setActiveFormats({})
    setPendingFile(null)
  }

  const handleKeyDown = (e) => {
    if (showMentions && mentionOptions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex(prev => (prev + 1) % mentionOptions.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex(prev => (prev - 1 + mentionOptions.length) % mentionOptions.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(mentionOptions[mentionIndex].username)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setShowMentions(false)
        return
      }
    }
    if (e.key === 'Escape' && editingMessage) {
      e.preventDefault()
      setEditingMessage(null)
      clearEditor(editorRef.current)
      setHasContent(false)
      return
    }
    if (e.key === 'Escape' && replyingToMessage) {
      e.preventDefault()
      setReplyingToMessage(null)
      return
    }
    // ↑ in the conversation's empty composer: edit your last message (the Mac's
    // composer does the same; a thread's or a new conversation's has its own onSend)
    if (e.key === 'ArrowUp' && !onSendProp && !editingMessage && !editorRef.current?.textContent.trim()) {
      const last = [...(activeMessages || [])].reverse().find((m) =>
        m.sender_id === currentUser?.id && m.sender_type !== 'agent' && !m.video_call && !m._unsent && !String(m.id).startsWith('temp-'))
      if (last) { e.preventDefault(); setEditingMessage(last); return }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
      switch (e.key) {
        case 'b': e.preventDefault(); applyFormat('bold'); break
        case 'i': e.preventDefault(); applyFormat('italic'); break
        case 'u': e.preventDefault(); applyFormat('underline'); break
        case 'e': e.preventDefault(); applyFormat('code'); break
        default: break
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
      switch (e.key.toLowerCase()) {
        case 'x': e.preventDefault(); applyFormat('strike'); break
        default: break
      }
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) setPendingFile(file)
    e.target.value = ''
  }

  const isImage = pendingFile && pendingFile.type?.startsWith('image/')
  const isPendingAudio = pendingFile && pendingFile.type?.startsWith('audio/')
  const [previewUrl, setPreviewUrl] = useState(null)
  const [pendingAudioSamples, setPendingAudioSamples] = useState(null)
  const [pendingAudioDuration, setPendingAudioDuration] = useState(0)
  const [pendingAudioPlaying, setPendingAudioPlaying] = useState(false)
  const [pendingAudioProgress, setPendingAudioProgress] = useState(0)
  const pendingAudioRef = useRef(null)
  const pendingAudioCanvasRef = useRef(null)
  const pendingAudioRafRef = useRef(null)

  useEffect(() => {
    if (isImage) {
      const url = URL.createObjectURL(pendingFile)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setPreviewUrl(null)
    }
  }, [pendingFile, isImage])

  // Decode pending audio file for waveform preview
  useEffect(() => {
    if (!isPendingAudio) {
      setPendingAudioSamples(null)
      setPendingAudioDuration(0)
      setPendingAudioPlaying(false)
      setPendingAudioProgress(0)
      if (pendingAudioRef.current) { pendingAudioRef.current.pause(); pendingAudioRef.current = null }
      cancelAnimationFrame(pendingAudioRafRef.current)
      return
    }
    let cancelled = false
    const url = URL.createObjectURL(pendingFile)
    const audio = new Audio(url)
    pendingAudioRef.current = audio
    audio.addEventListener('ended', () => {
      setPendingAudioPlaying(false)
      setPendingAudioProgress(0)
      cancelAnimationFrame(pendingAudioRafRef.current)
    })
    decodeAudioToSamples(pendingFile, 80).then(({ samples, duration }) => {
      if (cancelled) return
      setPendingAudioSamples(samples)
      setPendingAudioDuration(duration)
    }).catch(() => {})
    return () => {
      cancelled = true
      audio.pause()
      URL.revokeObjectURL(url)
    }
  }, [pendingFile, isPendingAudio])

  // Draw pending audio waveform
  useEffect(() => {
    const canvas = pendingAudioCanvasRef.current
    if (!canvas || !pendingAudioSamples) return
    const style = getComputedStyle(document.documentElement)
    drawWaveform(canvas, pendingAudioSamples, {
      progress: pendingAudioProgress,
      fill: true,
      playedColor: style.getPropertyValue('--accent')?.trim() || '#2d7ca8',
      unplayedColor: style.getPropertyValue('--text-faint')?.trim() || 'rgba(255,255,255,0.3)',
    })
  }, [pendingAudioSamples, pendingAudioProgress])

  function togglePendingAudioPlay() {
    const audio = pendingAudioRef.current
    if (!audio) return
    if (pendingAudioPlaying) {
      audio.pause()
      cancelAnimationFrame(pendingAudioRafRef.current)
    } else {
      audio.play()
      function tick() {
        if (audio.duration) setPendingAudioProgress(audio.currentTime / audio.duration)
        pendingAudioRafRef.current = requestAnimationFrame(tick)
      }
      pendingAudioRafRef.current = requestAnimationFrame(tick)
    }
    setPendingAudioPlaying(!pendingAudioPlaying)
  }

  // Just "Message" — DMs included (the header already names who you're
  // talking to; repeating it in the placeholder was noise, same call as
  // dropping the #channel name).
  const placeholder = placeholderProp || 'Message'

  return (
    <div
      ref={composerRef}
      className="composer-shell composer-shell--float block__foot-surface"
      data-qh-title="Message composer"
      data-qh-body="Write your message here. Press Enter to send, Shift+Enter for a new line. Supports markdown formatting."
    >

      <div className="message-input-container">
      {/* Link modal */}
      <Modal open={showLinkModal} onClose={() => setShowLinkModal(false)} title="Add link" size="md" className="link-modal">
            <div className="link-modal-body">
              <label className="link-modal-label">Text</label>
              <input
                className="link-modal-input"
                type="text"
                value={linkText}
                onChange={e => setLinkText(e.target.value)}
                placeholder="Display text"
                autoFocus
              />
              <label className="link-modal-label">Link</label>
              <input
                className="link-modal-input"
                type="text"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleLinkSave() } }}
              />
            </div>
            <div className="link-modal-actions">
              <button className="link-modal-btn link-modal-cancel" onClick={() => setShowLinkModal(false)}>Cancel</button>
              <button className="link-modal-btn link-modal-save" onClick={handleLinkSave} disabled={!linkUrl.trim()}>Save</button>
            </div>
      </Modal>
      {dragging && createPortal(
        <div className="drop-overlay">
          <div className="drop-overlay-ring" />
          <div className="drop-overlay-content">
            <svg className="drop-overlay-icon" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Drop to upload</span>
          </div>
        </div>,
        document.querySelector('.conversation') || document.body
      )}
      {replyingToMessage && !editingMessage && (
        <div className="reply-banner">
          <div className="reply-banner-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>
          </div>
          <div className="reply-banner-info">
            <span className="reply-banner-label">Replying to {replyingToMessage.sender?.username || 'Unknown'}</span>
            <span className="reply-banner-preview">
              {(replyingToMessage.content || '').length > 80 ? replyingToMessage.content.slice(0, 80) + '...' : replyingToMessage.content}
            </span>
          </div>
          <button className="reply-banner-close" onClick={() => setReplyingToMessage(null)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
      {editingMessage && (
        <div className="edit-banner">
          <div className="edit-banner-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
          <div className="edit-banner-info">
            <span className="edit-banner-label">Edit Message</span>
            <span className="edit-banner-preview">
              {editingMessage.content.length > 80 ? editingMessage.content.slice(0, 80) + '...' : editingMessage.content}
            </span>
          </div>
          <button className="edit-banner-close" onClick={() => { setEditingMessage(null); clearEditor(editorRef.current); setHasContent(false) }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
      {pendingFile && (
        isPendingAudio && pendingAudioSamples ? (
          <div className="voice-review">
            <div className="voice-review-waveform">
              <button className="voice-review-play" onClick={togglePendingAudioPlay}>
                {pendingAudioPlaying ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21" /></svg>
                )}
              </button>
              <canvas ref={pendingAudioCanvasRef} className="voice-waveform-canvas voice-review-canvas" onClick={(e) => {
                const audio = pendingAudioRef.current
                const canvas = pendingAudioCanvasRef.current
                if (!audio || !canvas || !audio.duration) return
                const rect = canvas.getBoundingClientRect()
                const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
                audio.currentTime = ratio * audio.duration
                setPendingAudioProgress(ratio)
              }} />
              <span className="voice-review-duration">{formatRecTime(Math.round(pendingAudioDuration))}</span>
            </div>
          </div>
        ) : (
          <div className="pending-file">
            {previewUrl ? (
              <img src={previewUrl} alt="Preview" className="pending-file-preview" />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            )}
            <span className="pending-file-name">{pendingFile.name}</span>
            <button className="pending-file-remove" onClick={() => setPendingFile(null)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )
      )}
      {showMentions && mentionOptions.length > 0 && (
        <div className="ofself-menu mention-popup">
          <div className="mention-popup-inner">
            {mentionOptions.map((m, i) => (
              <div
                key={m.user_id}
                className={`ofself-menu-item mention-option ${i === mentionIndex ? 'active' : ''}`}
                onMouseDown={e => { e.preventDefault(); insertMention(m.username) }}
                onMouseEnter={() => setMentionIndex(i)}
              >
                <span className="mention-avatar">
                  {m.username === 'everyone' ? '@' : m.username[0].toUpperCase()}
                </span>
                <span className="mention-name">
                  {m.username === 'everyone' ? '@everyone — notify all members' : m.username}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Plugin picker popup */}
      {showPluginPicker && (
        <PluginPicker
          query={pluginQuery}
          channelId={activeChannel?.id}
          onSelect={handlePluginSelect}
          onClose={() => setShowPluginPicker(false)}
        />
      )}
      {/* Emoji picker popup — PORTALED to <body> so it isn't a descendant of the
          backdrop-filtered .composer-shell (a nested backdrop-filter samples only inside
          that ancestor, so the lens would bend nothing). At body level its backdrop is the
          real app behind it, so the liquid-glass lens reads. Positioned over the composer
          via its rect; the composer is pinned at the panel bottom so the rect is stable. */}
      {showEmojiPicker && composerRef.current && createPortal(
        (() => {
          const r = composerRef.current.getBoundingClientRect()
          return (
            <div
              className="emoji-picker-portal"
              style={{ position: 'fixed', left: r.left, bottom: window.innerHeight - r.top, width: r.width }}
            >
              <EmojiPickerPanel onSelect={insertEmoji} />
            </div>
          )
        })(),
        document.body,
      )}
      {/* The + action strip — a full-width, boxy bar of the six actions flush above the
          input row (toggled by +). Cells are split by hairlines so each reads as a box. */}
      {actionsOpen && !recording && !reviewBlob && (
        <div className="composer-actions" ref={actionsRef}>
          <Tip label="Formatting" side="top">
            <button type="button" className={`composer-act-btn${showFormatting ? ' is-on' : ''}`} aria-label="Formatting"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setActionsOpen(false); setShowFormatting(f => !f) }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="4 7 4 4 20 4 20 7" />
                <line x1="9" y1="20" x2="15" y2="20" />
                <line x1="12" y1="4" x2="12" y2="20" />
              </svg>
            </button>
          </Tip>
          <Tip label="Emoji" side="top">
            <button ref={emojiBtnRef} type="button" className="composer-act-btn" aria-label="Emoji"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setActionsOpen(false); setShowEmojiPicker(v => !v); setShowMentions(false) }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="2.5" strokeLinecap="round" /><line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="2.5" strokeLinecap="round" /></svg>
            </button>
          </Tip>
          <Tip label="Mention" side="top">
            <button type="button" className="composer-act-btn" aria-label="Mention"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setActionsOpen(false); openMentionPopup() }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 0 0 6 0V12a10 10 0 1 0-3.92 7.94" /></svg>
            </button>
          </Tip>
          <Tip label="Audio Clip" side="top">
            <button type="button" className="composer-act-btn" aria-label="Audio Clip"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setActionsOpen(false); setShowMentions(false); setShowEmojiPicker(false); startRecording() }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>
            </button>
          </Tip>
          <Tip label="Video Clip" side="top">
            <button type="button" className="composer-act-btn" aria-label="Video Clip"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setActionsOpen(false); openVideoModal() }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
            </button>
          </Tip>
        </div>
      )}
      {/* Formatting toolbar — a full-width strip above the row (the ¶ action toggles it),
          the same boxy grammar as the + strip so the input line stays header-height. */}
      {showFormatting && !recording && !reviewBlob && (
        <div className="format-toolbar">
          <Tip label="Bold (Ctrl+B)"><button type="button" className={`format-btn ${activeFormats.bold ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); applyFormat('bold') }}>
            <strong style={{ fontFamily: 'var(--ofself-font-display)', fontSize: 'var(--ofself-text-2xl)', fontWeight: 'var(--ofself-weight-semibold)', letterSpacing: '0.02em' }}>B</strong>
          </button></Tip>
          <Tip label="Italic (Ctrl+I)"><button type="button" className={`format-btn ${activeFormats.italic ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); applyFormat('italic') }}>
            <em style={{ fontFamily: 'var(--ofself-font-display)', fontSize: 'var(--ofself-text-2xl)', fontWeight: 'var(--ofself-weight-semibold)', fontStyle: 'italic', letterSpacing: '0.02em' }}>I</em>
          </button></Tip>
          <Tip label="Underline (Ctrl+U)"><button type="button" className={`format-btn ${activeFormats.underline ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); applyFormat('underline') }}>
            <span style={{ fontFamily: 'var(--ofself-font-display)', fontSize: 'var(--ofself-text-2xl)', fontWeight: 'var(--ofself-weight-semibold)', textDecoration: 'underline', letterSpacing: '0.02em' }}>U</span>
          </button></Tip>
          <Tip label="Strikethrough"><button type="button" className={`format-btn ${activeFormats.strike ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); applyFormat('strike') }}>
            <span style={{ fontFamily: 'var(--ofself-font-display)', fontSize: 'var(--ofself-text-2xl)', fontWeight: 'var(--ofself-weight-semibold)', textDecoration: 'line-through', letterSpacing: '0.02em' }}>S</span>
          </button></Tip>
          <span className="format-divider" />
          <Tip label="Link"><button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); applyFormat('link') }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </button></Tip>
          <span className="format-divider" />
          <Tip label="Ordered List"><button type="button" className={`format-btn ${activeFormats.ol ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); applyFormat('ol') }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
              <line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" />
              <text x="3" y="8" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif" fontWeight="600">1</text>
              <text x="3" y="14" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif" fontWeight="600">2</text>
              <text x="3" y="20" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif" fontWeight="600">3</text>
            </svg>
          </button></Tip>
          <Tip label="Bulleted List"><button type="button" className={`format-btn ${activeFormats.ul ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); applyFormat('ul') }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
              <line x1="9" y1="6" x2="21" y2="6" /><line x1="9" y1="12" x2="21" y2="12" /><line x1="9" y1="18" x2="21" y2="18" />
              <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none" />
              <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none" />
              <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none" />
            </svg>
          </button></Tip>
          <span className="format-divider" />
          <Tip label="Inline Code (Ctrl+E)"><button type="button" className={`format-btn ${activeFormats.code ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); applyFormat('code') }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
              <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
            </svg>
          </button></Tip>
          <Tip label="Code Block"><button type="button" className={`format-btn ${activeFormats.codeblock ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); applyFormat('codeblock') }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
              <rect x="2" y="3" width="20" height="18" rx="2" />
              <polyline points="14 15 18 12 14 9" />
              <polyline points="10 9 6 12 10 15" />
            </svg>
          </button></Tip>
        </div>
      )}
      {/* One-row composer: [+ actions] [message…] [send]. */}
      <div className="composer-row">
      {/* Leading slot: + toggles the action strip; the audio review / pending-clip states
          swap in their own remove control; recording shows nothing (the rec bar carries
          its own controls). */}
      {recording ? null : reviewBlob ? (
        <button className="rec-action-btn rec-action-delete" onClick={cleanupReview}>Delete</button>
      ) : isPendingAudio && pendingAudioSamples ? (
        <button className="rec-action-btn rec-action-delete" onClick={() => setPendingFile(null)}>Remove</button>
      ) : doors ? null : (
        /* The + stands only where no rail holds its actions (a thread's composer, the phone).
           No <Tip> here — the rail's delegated giant tip already covers
           .composer-plus (RAIL_ITEM_SEL), single-sourced from the aria-label. */
        <button
          ref={plusRef}
          type="button"
          className={`composer-plus${actionsOpen ? ' is-on' : ''}`}
          aria-label="Add"
          aria-expanded={actionsOpen}
          onClick={() => setActionsOpen((v) => !v)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
      )}
      <div className="input-wrapper">
        {/* Content area - changes based on mode */}
        {recording ? (
          <div className="audio-rec-bar">
            <span className="audio-rec-dot" />
            <span className="audio-rec-time">{formatRecTime(recordingTime)}</span>
            <canvas ref={waveformCanvasRef} className="audio-rec-canvas" />
            <button className="rec-action-btn rec-action-delete" onClick={cancelRecording}>Cancel</button>
            <button className="rec-action-btn rec-action-stop" onClick={stopRecording}>Stop</button>
          </div>
        ) : reviewBlob ? (
          <div className="voice-review">
            <div className="voice-review-waveform">
              <button className="voice-review-play" onClick={toggleReviewPlay}>
                {reviewPlaying ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21" /></svg>
                )}
              </button>
              <canvas ref={reviewCanvasRef} className="voice-waveform-canvas voice-review-canvas" onClick={handleReviewCanvasClick} />
              <span className="voice-review-duration">{formatRecTime(Math.round(reviewDuration))}</span>
            </div>
          </div>
        ) : (
          <>
            {/* Editor area */}
            <div className="input-row">
              <div className="editor-wrap">
                <div
                  ref={editorRef}
                  className={`message-editor ${hasContent ? '' : 'is-empty'}`}
                  contentEditable
                  onInput={() => { updateHasContent(); checkForMention(); checkForSlashCommand(); if (activeChannel?.id) emitTyping(activeChannel.id) }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  data-placeholder={placeholder}
                  suppressContentEditableWarning
                />
              </div>
            </div>

          </>
        )}

        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
      </div>
        {/* Trailing slot: send (state-aware: Send while composing / on a review clip, Stop while recording). */}
        <Tip label={recording ? 'Stop' : 'Send'} side="top">
          <button
            type="button"
            className={`composer-act-btn send-btn ${(hasContent || pendingFile || reviewBlob) ? 'active' : ''}`}
            disabled={uploading}
            onClick={recording ? stopRecording : reviewBlob ? sendReview : handleSubmit}
          >
            {uploading ? (
              <span style={{ fontSize: 'var(--ofself-text-base)' }}>...</span>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            )}
          </button>
        </Tip>
      </div>
      {/* Video recording modal */}
      <Modal open={!!videoModal} onClose={cancelVideoRecording} label="Record video" className="video-modal" overlayClassName="video-modal-overlay" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="video-modal-viewport">
              {(videoModal === 'setup' || videoModal === 'recording') && (
                <>
                  <video ref={videoPreviewRef} className="video-modal-preview" muted playsInline />
                  {videoModal === 'recording' && (
                    <div className="video-modal-timer">
                      <span className="voice-recorder-dot" />
                      <span>{formatRecTime(videoTime)}</span>
                    </div>
                  )}
                </>
              )}
              {videoModal === 'review' && (
                <video ref={videoReviewRef} className="video-modal-preview" controls playsInline />
              )}
            </div>
            {videoModal === 'setup' && (
              <div className="video-modal-source-picker">
                <button className={`video-source-btn${videoSource === 'camera' ? ' active' : ''}`} onClick={() => setVideoSource('camera')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  Camera
                </button>
                <button className={`video-source-btn${videoSource === 'screen' ? ' active' : ''}`} onClick={() => setVideoSource('screen')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                  Screen
                </button>
              </div>
            )}
            <div className="video-modal-controls">
              {videoModal === 'setup' ? (
                <>
                  <Tip label="Cancel"><button className="rec-action-btn rec-action-delete" onClick={cancelVideoRecording}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button></Tip>
                  <Tip label="Record"><button className="rec-action-btn rec-action-record" onClick={startVideoRecording}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="12" r="7" /></svg>
                  </button></Tip>
                </>
              ) : videoModal === 'recording' ? (
                <>
                  <Tip label="Cancel"><button className="rec-action-btn rec-action-delete" onClick={cancelVideoRecording}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  </button></Tip>
                  <Tip label="Stop"><button className="rec-action-btn rec-action-stop" onClick={stopVideoRecording}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                  </button></Tip>
                </>
              ) : (
                <>
                  <Tip label="Delete"><button className="rec-action-btn rec-action-delete" onClick={cancelVideoRecording}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  </button></Tip>
                  <Tip label="Send"><button className="send-btn active" onClick={sendVideoClip}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button></Tip>
                </>
              )}
            </div>
      </Modal>
      </div>
    </div>
  )
}
